<template>
  <div class="p-3 pb-16 bg mb-auto h-full overflow-auto bg-gradient-to-b from-gray-800 text-white" id="style-1">
    <!-- Header -->
    <header v-if="isAdmin" class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex flex-wrap justify-between items-center gap-2 max-w-7xl mx-auto">
        <h1 class="text-xl font-semibold text-indigo-300">จัดการสิ่งมีชีวิตกลายพันธุ์</h1>
        <div class="flex flex-wrap items-center gap-2">
          <TemplateExportButton
            collection-name="gameMutants"
            file-name="gameMutants.json"
            label="Export gameMutants.json"
          />
        <button 
          @click="toggleModal = 'AddMutantModal'" 
          class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          aria-label="เพิ่มสิ่งมีชีวิตกลายพันธุ์ใหม่"
        >
          <i class="fas fa-plus-circle text-xs"></i> เพิ่มสิ่งมีชีวิตกลายพันธุ์ใหม่
        </button>
        </div>
      </div>
    </header>

    <!-- Tabs -->
    <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
      <div class="flex space-x-4">
        <button 
          @click="setActiveTab('filter')" 
          :class="{'bg-indigo-600': activeTab === 'filter', 'bg-gray-700': activeTab !== 'filter'}" 
          class="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200"
        >
          ตัวกรองสิ่งมีชีวิตกลายพันธุ์
        </button>
        <button 
          @click="setActiveTab('recent')" 
          :class="{'bg-indigo-600': activeTab === 'recent', 'bg-gray-700': activeTab !== 'recent'}" 
          class="px-4 py-2 rounded-md text-sm font-medium text-white transition-colors duration-200"
        >
          สิ่งมีชีวิตกลายพันธุ์เพิ่มล่าสุด
        </button>
      </div>
    </div>

    <!-- Filters Tab -->
    <section v-if="activeTab === 'filter'" class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">ตัวกรองสิ่งมีชีวิตกลายพันธุ์</h3>
        <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <!-- Location (Required) -->
          <div>
            <label class="block text-sm font-medium text-indigo-200 mb-1">เลือกสถานที่ (จำเป็น)</label>
            <select 
              v-model="filterLocation" 
              @change="applyFilters" 
              class="w-full px-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              aria-label="เลือกสถานที่สิ่งมีชีวิตกลายพันธุ์"
            >
              <option value="">เลือกสถานที่</option>
              <option v-for="loc in locations" :key="loc" :value="loc">{{ loc }}</option>
            </select>
          </div>

          <!-- Name Search -->
          <div class="relative col-span-2 sm:col-span-2">
            <label class="block text-sm font-medium text-indigo-200 mb-1">ค้นหาชื่อสิ่งมีชีวิตกลายพันธุ์</label>
            <i class="fas fa-magnifying-glass absolute left-3 top-10 text-indigo-400"></i>
            <input 
              v-model="filterName" 
              @input="debouncedApplyFilters" 
              type="text" 
              placeholder="ค้นหาชื่อสิ่งมีชีวิตกลายพันธุ์..." 
              class="w-full pl-10 pr-3 py-2 bg-gray-800 text-indigo-200 rounded-md border border-gray-600 focus:outline-none focus:ring-2 focus:ring-indigo-400 transition duration-200"
              :disabled="!filterLocation"
              aria-label="ค้นหาชื่อสิ่งมีชีวิตกลายพันธุ์"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Recent Mutants Tab -->
    <section v-if="activeTab === 'recent'" class="w-full">
      <div class="bg-gray-900 bg-opacity-80 p-4 border rounded mb-1 border-gray-800 shadow-xl">
        <h3 class="text-sm font-semibold text-indigo-200 mb-3">สิ่งมีชีวิตกลายพันธุ์ที่เพิ่มล่าสุด</h3>
        <div v-if="recentMutants.length === 0 && !isRecentLoading" class="text-center text-indigo-200 py-6">
          <i class="fas fa-virus text-2xl mb-2"></i>
          <p class="text-sm">ไม่พบสิ่งมีชีวิตกลายพันธุ์ที่เพิ่มล่าสุด</p>
        </div>
        <div v-else class="overflow-x-auto">
          <table class="min-w-full divide-y divide-gray-700 text-sm text-indigo-200">
            <thead class="bg-gray-800">
              <tr>
                <th class="px-4 py-2 text-left font-medium">รูปภาพ</th>
                <th class="px-4 py-2 text-left font-medium">ชื่อ</th>
                <th class="px-4 py-2 text-left font-medium">จับได้?</th>
                <th class="px-4 py-2 text-left font-medium">ระดับความยากในการจับ</th>
                <th class="px-4 py-2 text-left font-medium">โอกาสจับ (%)</th>
                <th class="px-4 py-2 text-left font-medium">Sta</th>
                <th class="px-4 py-2 text-left font-medium">Str</th>
                <th class="px-4 py-2 text-left font-medium">Int</th>
                <th class="px-4 py-2 text-left font-medium">Agi</th>
                <th class="px-4 py-2 text-left font-medium">Def</th>
                <th class="px-4 py-2 text-left font-medium">Luck</th>
                <th class="px-4 py-2 text-left font-medium">เพิ่มเมื่อ</th>
                <th v-if="isAdmin" class="px-4 py-2 text-left font-medium">จัดการ</th>
              </tr>
            </thead>
            <tbody class="bg-gray-900 divide-y divide-gray-700">
              <tr v-for="mutant in displayedRecentMutants" :key="mutant.id" class="hover:bg-gray-700 transition-colors duration-200">
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">
                  <img 
                    :src="mutant.img || 'default-mutant.png'" 
                    alt="Mutant Image" 
                    class="w-16 h-16 object-cover rounded-md border-2 border-indigo-400"
                    @error="handleImageError"
                  />
                </td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">
                  <div class="flex items-center justify-between">
                    <span>{{ mutant.name }}</span>
                    <i 
                      v-if="isAdmin" 
                      class="fas fa-copy text-xs text-indigo-200 cursor-pointer hover:text-indigo-400 transition-colors duration-200" 
                      @click.stop="copyName(mutant.name)"
                      aria-label="คัดลอกชื่อสิ่งมีชีวิตกลายพันธุ์"
                    ></i>
                  </div>
                </td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.canCatch ? 'ใช่' : 'ไม่' }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.catchDifficulty || '-' }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.canCatch ? getCatchChance(mutant.catchDifficulty) : '-' }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.stats?.stamina || 0 }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.stats?.str || 0 }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.stats?.int || 0 }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.stats?.agi || 0 }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.stats?.defense || 0 }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ mutant.stats?.luck || 0 }}</td>
                <td class="px-4 py-2 cursor-pointer" @click="openDetailsModal(mutant)">{{ formatDate(mutant.createdAt) }}</td>
                <td v-if="isAdmin" class="px-4 py-2">
                  <button 
                    @click="openEditModal(mutant)" 
                    class="bg-indigo-600 hover:bg-indigo-700 text-white py-1 px-3 rounded-md transition-colors duration-200"
                  >
                    แก้ไข
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Load More Button for Recent Mutants -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMoreRecent && !isRecentLoading" 
            @click="loadMoreRecent" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดสิ่งมีชีวิตกลายพันธุ์เพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isRecentLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMoreRecent && recentMutants.length > 0" class="text-indigo-200 text-sm">ไม่มีสิ่งมีชีวิตกลายพันธุ์เพิ่มเติมแล้ว</span>
        </div>
      </div>
    </section>

    <!-- Main Content for Filter Tab -->
    <main v-if="activeTab === 'filter'" class="max-w-7xl mx-auto">
      <!-- Error Message -->
      <div v-if="error" class="text-red-300 text-sm text-center mb-4">
        {{ error }}
      </div>

      <!-- Prompt to Select Location -->
      <div v-if="!filterLocation" class="text-center text-indigo-200 py-6">
        <i class="fas fa-virus text-2xl mb-2"></i>
        <p class="text-sm">กรุณาเลือกสถานที่เพื่อแสดงรายการ</p>
      </div>

      <div v-else>
        <div class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
          <div 
            v-for="mutant in displayedMutants" 
            :key="mutant.id" 
            @click="openDetailsModal(mutant)"
            class="bg-gray-900 rounded-md p-4 border border-gray-800 bg-opacity-80 hover:bg-opacity-80 transition-all duration-200 hover:bg-gray-600 hover:border-indigo-400 cursor-pointer shadow-xl"
          >
            <!-- Header -->
            <div class="flex items-center space-x-3 mb-3">
              <img 
                :src="mutant.img || 'default-mutant.png'" 
                alt="Mutant Image" 
                class="w-16 h-16 object-cover rounded-md border-2 border-indigo-400"
                @error="handleImageError"
              />
              <div>
                <div class="flex items-center justify-between">
                  <h3 
                    class="text-sm font-semibold text-indigo-200"
                  >
                    {{ mutant.name }} ({{ (mutant.chance / 100).toFixed(2) }}%)
                  </h3>
                  <i 
                    v-if="isAdmin" 
                    class="fas fa-copy text-xs text-indigo-200 cursor-pointer hover:text-indigo-400 transition-colors duration-200" 
                    @click.stop="copyName(mutant.name)"
                    aria-label="คัดลอกชื่อสิ่งมีชีวิตกลายพันธุ์"
                  ></i>
                </div>
              </div>
            </div>

            <!-- Stats -->
            <div v-if="mutant.stats" class="border-t border-gray-600 pt-3 mb-3">
              <h4 class="text-xs font-medium text-gray-300 mb-1">ค่าสถานะ</h4>
              <div class="grid grid-cols-3 gap-1 text-xs text-gray-300">
                <div class="flex items-center space-x-1">
                  <i class="fas fa-heart text-red-500"></i>
                  <span>{{ mutant.stats.stamina }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-dumbbell text-yellow-500"></i>
                  <span>{{ mutant.stats.str }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-brain text-blue-500"></i>
                  <span>{{ mutant.stats.int }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-running text-green-500"></i>
                  <span>{{ mutant.stats.agi }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-clover text-green-300"></i>
                  <span>{{ mutant.stats.luck }}</span>
                </div>
                <div class="flex items-center space-x-1">
                  <i class="fas fa-shield-alt text-purple-500"></i>
                  <span>{{ mutant.stats.defense }}</span>
                </div>
              </div>
            </div>
            <!-- Edit Button -->
            <div v-if="isAdmin" class="mt-3">
              <button 
                @click.stop="openEditModal(mutant)" 
                class="w-full bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-3 rounded-md transition-colors duration-200 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                aria-label="แก้ไขสิ่งมีชีวิตกลายพันธุ์"
              >
                <i class="fas fa-edit text-xs"></i> แก้ไข
              </button>
            </div>
          </div>
        </div>

        <!-- No Mutants Message -->
        <div v-if="displayedMutants.length === 0 && !isLoading && filterLocation" class="text-center text-indigo-200 py-6">
          <i class="fas fa-virus text-2xl mb-2"></i>
          <p class="text-sm">ไม่พบสิ่งมีชีวิตกลายพันธุ์ที่ตรงกับการค้นหา</p>
        </div>

        <!-- Load More Button -->
        <div class="mt-6 flex justify-center">
          <button 
            v-if="hasMore && !isLoading" 
            @click="loadMore" 
            class="bg-indigo-600 hover:bg-indigo-700 text-sm text-white py-2 px-4 rounded-md transition-colors duration-200 flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            aria-label="โหลดเพิ่มเติม"
          >
            <i class="fas fa-arrow-down text-xs"></i> โหลดเพิ่มเติม
          </button>
          <div v-if="isLoading" class="flex items-center justify-center py-4">
            <div class="animate-spin rounded-full h-8 w-8 border-t-2 border-indigo-600"></div>
          </div>
          <span v-if="!hasMore && displayedMutants.length > 0" class="text-indigo-200 text-sm">ไม่มีสิ่งมีชีวิตกลายพันธุ์เพิ่มเติมแล้ว</span>
        </div>
      </div>
    </main>

    <!-- Modals -->
    <AddMutantModal 
      v-if="toggleModal === 'AddMutantModal'" 
      @cancelModal="toggleModal = ''" 
      @mutantAdded="resetAndFetch" 
    />
    <EditMutantModal 
      v-if="toggleModal === 'EditMutantModal'" 
      :mutant-id="selectedMutantId" 
      @cancelModal="toggleModal = ''" 
      @mutantUpdated="resetAndFetch" 
    />
    <DetailMutantModal 
      v-if="toggleModal === 'DetailMutantModal'" 
      :mutant-id="selectedMutantId" 
      @cancelModal="toggleModal = ''" 
      @editMutant="openEditModalFromDetail"
    />
  </div>
</template>

<script>
import { ref, computed, onMounted, watch } from 'vue';
import { usePlayerStore } from '@/composables/usePlayerStore';
import AddMutantModal from '../components/admin/AddMutantModal.vue';
import EditMutantModal from '../components/admin/EditMutantModal.vue';
import DetailMutantModal from '../components/admin/DetailMutantModal.vue';
import TemplateExportButton from '../components/admin/TemplateExportButton.vue';
import gameMutantsData from '@/data/gameMutants.json';
import { projectFirestore } from '@/firebase/config';
import getUser from '@/composables/getUser';

export default {
  components: { AddMutantModal, EditMutantModal, DetailMutantModal, TemplateExportButton },
  setup() {
    const toggleModal = ref('');
    const mutants = ref([]);
    const recentMutants = ref([]);
    const filterName = ref('');
    const filterLocation = ref('');
    const selectedMutantId = ref(null);
    const isLoading = ref(false);
    const isRecentLoading = ref(false);
    const isAdmin = ref(false);
    const error = ref('');
    const mutantsPerLoad = 60;
    const currentPage = ref(1);
    const recentPage = ref(1);
    const maps = ref([]);
    const activeTab = ref('filter');

    // Define the custom order for locations (maps)
    const customMapOrder = [
      'ตรอกซอย', 'ตลาด', 'สนามกอล์ฟ', 'โรงเรียน', 'มหาวิทยาลัย', 'โกดัง', 'รถไฟฟ้าใต้ดิน',
      'โรงพยาบาล', 'สยามสแควร์', 'หมู่บ้านคนรวย', 'สวนสนุก', 'โรงงานอุตสาหกรรม', 'ห้างสรรพสินค้า',
      'สถานีตำรวจ', 'หอสมุดแห่งชาติ', 'สถานีดับเพลิงร้าง', 'ค่ายทหาร','สถาบันวิจัยหุ่นยนต์','สถาบันวิจัยไวรัส', 'พระบรมมหาราชวัง', 'ป่าช้า', 'สุสานนักรบโบราณ'
    ];

    const locations = computed(() => {
      return maps.value
        .map(m => m.name)
        .filter(name => typeof name === 'string')
        .sort((a, b) => {
          const indexA = customMapOrder.indexOf(a);
          const indexB = customMapOrder.indexOf(b);
          if (indexA !== -1 && indexB !== -1) return indexA - indexB;
          if (indexA !== -1) return -1;
          if (indexB !== -1) return 1;
          return a.localeCompare(b);
        });
    });

    const { user } = getUser();

    // Debounce function for name search
    const debounce = (func, wait) => {
      let timeout;
      return (...args) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
      };
    };

    // Handle image load errors
    const handleImageError = (event) => {
      event.target.src = 'default-mutant.png';
    };

    // Format date for display
    const formatDate = (timestamp) => {
      if (!timestamp) return 'ไม่ระบุ';
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString('th-TH', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    };

    const playerStore = usePlayerStore();

    watch(() => playerStore.userData, (data) => {
      if (data) {
        isAdmin.value = data.admin === true;
      } else {
        isAdmin.value = false;
      }
    }, { immediate: true });

    // Fetch maps to get locations
    const fetchMaps = async () => {
      try {
        error.value = null;
        let mapsQuery = projectFirestore.collection('maps');
        if (!isAdmin.value) {
          mapsQuery = mapsQuery.where('isActive', '==', true);
        }
        const mapsSnapshot = await mapsQuery.get();

        maps.value = mapsSnapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            name: typeof data.name === 'string' ? data.name : 'Unknown',
            isActive: data.isActive !== undefined ? data.isActive : true,
            ...data
          };
        });
      } catch (err) {
        error.value = 'ไม่สามารถดึงข้อมูลแผนที่ได้';
        console.error('Error fetching maps:', err);
      }
    };

    const getTime = (createdAt) => {
      if (!createdAt) return 0;
      if (createdAt.toDate) return createdAt.toDate().getTime();
      if (createdAt.seconds) return createdAt.seconds * 1000;
      return new Date(createdAt).getTime();
    };

    // Fetch mutants from JSON with filters
    const fetchMutants = async () => {
      if (!filterLocation.value) {
        mutants.value = [];
        isLoading.value = false;
        return;
      }

      try {
        isLoading.value = true;
        error.value = '';
        const mapQuery = projectFirestore.collection('maps')
          .where('name', '==', filterLocation.value)
          .limit(1);

        const mapSnapshot = await mapQuery.get();

        if (mapSnapshot.empty) {
          mutants.value = [];
          isLoading.value = false;
          return;
        }

        const mapData = mapSnapshot.docs[0].data();
        const mapMutants = mapData.mutants || [];

        let newMutants = mapMutants.map(m => {
          const data = gameMutantsData[m.name];
          if (!data) return null;
          return { id: m.name, ...data, chance: m.chance };
        }).filter(m => m !== null);

        if (filterName.value) {
          newMutants = newMutants.filter(mutant =>
            mutant.name.toLowerCase().includes(filterName.value.toLowerCase())
          );
        }

        // Sort by chance descending
        newMutants.sort((a, b) => b.chance - a.chance);

        mutants.value = newMutants;
        currentPage.value = 1;
      } catch (error) {
        console.error('Error fetching mutants:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลสิ่งมีชีวิตกลายพันธุ์';
      } finally {
        isLoading.value = false;
      }
    };

    // Fetch recent mutants from JSON
    const fetchRecentMutants = async () => {
      try {
        isRecentLoading.value = true;
        error.value = '';

        recentMutants.value = Object.entries(gameMutantsData)
          .map(([id, data]) => ({
            id,
            ...data,
            createdAt: data.createdAt || null
          }))
          .filter(mutant => mutant.createdAt)
          .sort((a, b) => getTime(b.createdAt) - getTime(a.createdAt));

        recentPage.value = 1;
      } catch (error) {
        console.error('Error fetching recent mutants:', error);
        error.value = 'เกิดข้อผิดพลาดในการโหลดข้อมูลสิ่งมีชีวิตกลายพันธุ์ล่าสุด';
      } finally {
        isRecentLoading.value = false;
      }
    };

    // Compute displayed mutants for pagination
    const displayedMutants = computed(() => {
      const end = currentPage.value * mutantsPerLoad;
      return mutants.value.slice(0, end);
    });

    const displayedRecentMutants = computed(() => {
      const end = recentPage.value * mutantsPerLoad;
      return recentMutants.value.slice(0, end);
    });

    const hasMore = computed(() => {
      return (currentPage.value * mutantsPerLoad) < mutants.value.length;
    });

    const hasMoreRecent = computed(() => {
      return (recentPage.value * mutantsPerLoad) < recentMutants.value.length;
    });

    // Apply filters and reset
    const applyFilters = () => {
      mutants.value = [];
      currentPage.value = 1;
      fetchMutants();
    };

    // Debounced version of applyFilters for name search
    const debouncedApplyFilters = debounce(applyFilters, 300);

    // Load more mutants
    const loadMore = () => {
      currentPage.value += 1;
    };

    const loadMoreRecent = () => {
      recentPage.value += 1;
    };

    // Reset and fetch mutants
    const resetAndFetch = () => {
      mutants.value = [];
      currentPage.value = 1;
      recentMutants.value = [];
      recentPage.value = 1;
      fetchMutants();
      if (activeTab.value === 'recent') {
        fetchRecentMutants();
      }
    };

    // Set active tab and fetch data if necessary
    const setActiveTab = (tab) => {
      activeTab.value = tab;
      if (tab === 'recent') {
        recentMutants.value = [];
        recentPage.value = 1;
        fetchRecentMutants();
      }
    };

    // Open edit modal
    const openEditModal = (mutant) => {
      selectedMutantId.value = mutant.id;
      toggleModal.value = 'EditMutantModal';
    };

    // Open details modal
    const openDetailsModal = (mutant) => {
      selectedMutantId.value = mutant.id;
      toggleModal.value = 'DetailMutantModal';
    };

    // Open edit from detail
    const openEditModalFromDetail = (mutantId) => {
      selectedMutantId.value = mutantId;
      toggleModal.value = 'EditMutantModal';
    };

    const getCatchChance = (difficulty) => {
      switch (difficulty) {
        case 1: return 5;
        case 2: return 4;
        case 3: return 3;
        case 4: return 2;
        case 5: return 1;
        default: return 5;
      }
    };

    // Copy mutant name to clipboard
    const copyName = (name) => {
      navigator.clipboard.writeText(name)
        .catch(err => {
          console.error('Failed to copy name:', err);
          alert('เกิดข้อผิดพลาดในการคัดลอกชื่อสิ่งมีชีวิตกลายพันธุ์');
        });
    };

    // Fetch maps and recent mutants on mount
    onMounted(async () => {
      await fetchMaps();
      if (isAdmin.value && activeTab.value === 'recent') {
        await fetchRecentMutants();
      }
    });

    return { 
      toggleModal, 
      filterName, 
      filterLocation, 
      mutants, 
      recentMutants, 
      displayedMutants, 
      displayedRecentMutants, 
      selectedMutantId, 
      openEditModal, 
      openDetailsModal, 
      openEditModalFromDetail,
      loadMore, 
      loadMoreRecent,
      hasMore, 
      hasMoreRecent,
      isLoading, 
      isRecentLoading,
      isAdmin, 
      resetAndFetch, 
      applyFilters, 
      debouncedApplyFilters, 
      error, 
      handleImageError,
      locations,
      activeTab,
      formatDate,
      setActiveTab,
      getCatchChance,
      copyName
    };
  },
};
</script>

<style scoped>
/* Custom Scrollbar */
::-webkit-scrollbar {
  width: 5px;
}

::-webkit-scrollbar-track {
  background: #374151;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb {
  background: #4b5563;
  border-radius: 10px;
}

::-webkit-scrollbar-thumb:hover {
  background: #6b7280;
}

/* Animations */
.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

button:disabled, input:disabled, select:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

button:not(:disabled):active {
  transform: scale(0.98);
}
.bg {
  background: url('https://firebasestorage.googleapis.com/v0/b/zombiekub.firebasestorage.app/o/bg%2Fmanagerroom.png?alt=media&token=6b9348b6-9364-4ce5-bf58-6e7b9c5b12f1') no-repeat center center fixed,
              linear-gradient(to bottom, rgba(31, 41, 55, 0.8), rgba(0, 0, 0, 0.9));
  background-size: cover;
}
</style>
